import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { getCanonicalUrl } from '../utils/canonical'
import {
  CheckCircle,
  
  Shield,
  Zap,
  ChevronDown,
  ChevronUp,
  
} from 'lucide-react'
import InlineScanSection from '../components/InlineScanSection'

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const quickWins = [
    'Add missing form labels: Ensure every input field has a programmatically associated label.',
    'Write a descriptive page title: The <title> tag should accurately describe the page content.',
    'Fix low color contrast: Use a color contrast checker to ensure text is readable.',
    'Eliminate keyboard focus traps: Make sure a user can navigate to and from every interactive element using only a keyboard.',
    'Remove empty links: Anchor tags without text or an accessible name are confusing for screen reader users.',
    'Provide meaningful alt text: Describe the content and function of every informative image.'
  ]

  const complianceData = [
    {
      issue: 'Image missing alt text',
      wcag: '1.1.1 Non-text Content (A)',
      standards: 'ADA, Section 508, EN 301 549'
    },
    {
      issue: 'Low contrast text',
      wcag: '1.4.3 Contrast (Minimum) (AA)',
      standards: 'ADA, Section 508, EN 301 549'
    },
    {
      issue: 'Unlabeled form field',
      wcag: '3.3.2 Labels or Instructions (A)',
      standards: 'ADA, Section 508, EN 301 549'
    }
  ]

  const faqs = [
    {
      question: 'What is a web accessibility checker (and why use achecker.ca)?',
      answer: 'A web accessibility checker is a tool that automatically scans a webpage for violations of accessibility standards like WCAG. You should use the free web accessibility checker at achecker.ca for an instant, reliable first-pass audit of your site\'s conformance with global standards.'
    },
    {
      question: 'Does the achecker.ca accessibility checker validate against WCAG 2.2?',
      answer: 'Yes. Our checks are based on the latest W3C recommendation, WCAG 2.2, with a focus on Level A and AA criteria, which are the most common targets for legal compliance.'
    },
    {
      question: 'Is a 100 Accessibility Score the same as ADA compliance?',
      answer: 'No. A 100 score indicates that our automated tool found no detectable WCAG violations, which is a strong indicator of technical accessibility. However, full ADA website accessibility requires manual testing to ensure a truly usable experience for people with disabilities, which no automated tool can guarantee.'
    },
    {
      question: 'How does this relate to Section 508 in the US?',
      answer: 'Section 508 requires US federal agencies and their contractors to adhere to WCAG 2.0 AA. Our checker tests against these criteria (and more recent ones), making it a valuable tool for identifying issues relevant to Section 508 conformance.'
    },
    {
      question: 'Do EU sites have to meet EN 301 549 / WCAG?',
      answer: 'Yes, public sector websites and mobile apps in the European Union must conform to the EN 301 549 standard, which directly incorporates WCAG 2.1 AA. Our WCAG checks cover these requirements.'
    },
    {
      question: 'Does the achecker.ca web accessibility checker cover keyboard accessibility?',
      answer: 'Our tool can detect some common keyboard accessibility issues, such as visible focus indicators and certain focus traps. However, comprehensive keyboard testing—ensuring a logical and intuitive navigation path—requires manual review.'
    },
    {
      question: 'Will any checker find every WCAG issue?',
      answer: 'No. Industry estimates suggest automated checkers can find only 30-40% of all potential WCAG issues. Issues related to context, user experience, and logic always require a human to test and validate.'
    },
    {
      question: 'Which WCAG levels does achecker.ca focus on (A/AA/AAA)?',
      answer: 'achecker.ca primarily focuses on Level A and Level AA success criteria, as these form the basis of most international accessibility laws and regulations.'
    },
    {
      question: 'Is the achecker.ca accessibility checker free?',
      answer: 'Yes, the on-page web accessibility checker at achecker.ca is completely free to use for scanning individual pages.'
    }
  ]

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index)
  }

  return (
    <>
      <Helmet>
        <title>Free Web Accessibility Checker – WCAG Audit | achecker.ca</title>
        <meta name="description" content="Run a free web accessibility checker on achecker.ca. Get an instant Accessibility Score, WCAG-based fixes, screen reader & keyboard checks, and color-contrast tips." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={getCanonicalUrl('/')} />
      </Helmet>

      <div className="space-y-12">
        {/* Hero Section */}
        <section className="text-center py-12 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Free Web Accessibility Checker – Instant WCAG 2.2 Audit & Accessibility Score
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-4xl mx-auto">
            Run a <strong>free web accessibility checker on achecker.ca</strong>. In seconds, the achecker.ca accessibility checker gives you an Accessibility Score and WCAG-based fixes trusted worldwide for ADA, Section 508, and EU/EN 301 549 compliance. Use this accessibility checker to prioritize real-world improvements—and re-scan on achecker.ca anytime to track progress.
          </p>
          <p className="text-sm text-gray-500 mb-8 italic">
            Automated tests are a first pass; full conformance requires human review.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#scan-section"
              className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              onClick={(e) => {
                e.preventDefault()
                document.getElementById('scan-section')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              <Zap className="w-5 h-5 mr-2" />
              Start Free Testing
            </a>
            <Link
              to="/wcag-validator"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-gray-700 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <Shield className="w-5 h-5 mr-2" />
              WCAG Validator
            </Link>
          </div>
        </section>

        {/* Inline Scan Section */}
        <InlineScanSection />

        {/* What the tool does */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What the achecker.ca accessibility checker does in seconds</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-6">
              A web accessibility checker is an automated tool that scans your webpage against a set of accessibility rules. The achecker.ca tool instantly analyzes your code and content, providing an <strong>Accessibility Score from 0 to 100</strong>. It highlights specific WCAG failures and presents a prioritized list of issues, so you know exactly where to start. We help you find the quick wins that immediately improve usability for everyone.
            </p>
            <p className="text-gray-600">
              Remember, no automated tool can replace a human. Our accessibility checker provides an essential first-pass analysis, but comprehensive compliance always requires manual testing by accessibility experts.
            </p>
          </div>
        </section>

        {/* WCAG Information */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Why WCAG matters globally (and why achecker.ca uses WCAG 2.2)</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">WCAG 2.2 overview (A/AA focus)</h3>
              <p className="text-gray-700 mb-4">
                Developed by the World Wide Web Consortium (W3C), WCAG is organized into three conformance levels: A (essential), AA (the common legal standard), and AAA (highest). achecker.ca focuses primarily on Level A and AA criteria from the latest version, WCAG 2.2, as this level is the target for most organizations and regulations.
              </p>
              
              <h4 className="font-semibold text-gray-900 mb-2">What checks catch</h4>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Missing alternative text (alt text) for images</li>
                <li>Missing form input labels</li>
                <li>Incorrect heading structure and missing page landmarks</li>
                <li>Insufficient color contrast between text and background</li>
                <li>Links or buttons without a clear purpose</li>
                <li>Missing page titles</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">What needs manual review</h4>
              <p className="text-gray-700 mb-4">
                Automated scanning can't understand context or user experience. Critical areas that always require manual testing include:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Logical and intuitive keyboard accessibility journeys</li>
                <li>Real-world screen reader compatibility and usability</li>
                <li>The clarity and intent of your content and link text</li>
                <li>Complex interactions like dynamic menus or multi-step forms</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Compliance Snapshot */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Compliance snapshot</h2>
          <p className="text-gray-700 mb-6">
            achecker.ca helps you meet standards across key jurisdictions. Here's a brief overview:
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">USA: ADA & Section 508</h3>
              <p className="text-gray-700 text-sm">
                The Americans with Disabilities Act (ADA) requires businesses to provide accessible digital experiences. While not explicitly naming a standard, courts consistently reference WCAG. Section 508 requires federal agencies to conform to WCAG 2.0 AA.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">European Union: EN 301 549 & EAA</h3>
              <p className="text-gray-700 text-sm">
                The EU Web Accessibility Directive mandates public sector websites and apps meet the EN 301 549 standard, which incorporates WCAG 2.1 AA. The upcoming European Accessibility Act (EAA) will extend similar requirements to the private sector from June 28, 2025.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">United Kingdom</h3>
              <p className="text-gray-700 text-sm">
                The Public Sector Bodies (Websites and Mobile Applications) Accessibility Regulations 2018 require UK public sector bodies to meet WCAG 2.1 AA.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Canada</h3>
              <p className="text-gray-700 text-sm">
                Ontario's Accessibility for Ontarians with Disabilities Act (AODA) requires many public and private sector websites to meet WCAG 2.0 AA. The federal government has adopted a similar standard under the Accessible Canada Act.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Australia & New Zealand</h3>
              <p className="text-gray-700 text-sm">
                Australia's Disability Discrimination Act 1992 is supported by the National Transition Strategy, which advises public agencies to meet WCAG 2.0 AA. New Zealand's government requires WCAG 2.1 AA conformance, with an update to WCAG 2.2 AA planned for 2025.
              </p>
            </div>
          </div>
        </section>

        {/* Quick Wins */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Quick wins after your achecker.ca scan</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-6">
              After you run our accessibility checker, you'll have a clear list of improvements. Focus on these high-impact fixes first:
            </p>
            <div className="space-y-4">
              {quickWins.map((win, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">{win}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-700 mt-6 font-medium">
              Once you've made changes, <strong>run an accessibility checker (WCAG 2.2) on achecker.ca again</strong> to see your Accessibility Score improve!
            </p>
          </div>
        </section>

        {/* Accessibility Score Mapping */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How your Accessibility Score maps to obligations</h2>
          <p className="text-gray-700 mb-6">
            Our score directly reflects issues tied to global standards. While a high score is not a legal guarantee, it shows a strong, good-faith effort to align with the technical criteria referenced in laws worldwide. <strong>achecker.ca – your WCAG accessibility checker for ADA/Section 508/EU</strong>.
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-sm border border-gray-200">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Issue Example</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">WCAG 2.2 Criterion</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Where It Matters</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {complianceData.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{item.issue}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{item.wcag}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{item.standards}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Trust & Privacy */}
        <section className="bg-gray-50 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Trust & privacy</h2>
          <p className="text-gray-700 mb-4">
            Your privacy is paramount. Scans performed on achecker.ca are anonymous. We do not store, share, or sell the URLs you test or the results generated. Your data is your own.
          </p>
          <p className="text-sm text-gray-600 italic">
            Disclaimer: The information on this page and the results from our tool do not constitute legal advice. Consult with a qualified legal professional for guidance on specific compliance obligations.
          </p>
        </section>

        {/* FAQ Section */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions (FAQ)</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-medium text-gray-900">{faq.question}</h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center py-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl text-white">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Make Your Website Accessible?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Start testing now with our free WCAG 2.2 accessibility checker and ensure your website is inclusive for all users
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#scan-section"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              onClick={(e) => {
                e.preventDefault()
                document.getElementById('scan-section')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              <Zap className="w-5 h-5 mr-2" />
              Start Free Testing
            </a>
            <Link
              to="/ada-compliance-checker"
              className="inline-flex items-center justify-center px-8 py-3 bg-blue-700 text-white rounded-lg font-semibold hover:bg-blue-800 transition-colors border border-blue-500"
            >
              <Shield className="w-5 h-5 mr-2" />
              ADA Compliance Checker
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}
